'use client'
import { useEffect, useRef } from 'react'

import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import { trackReferralError, trackReferralRedemptionPopupOpened } from '@src/telemetry/referral-telemetry'

// Yotpo/Swell referral share links land with one of these params. Only then is the Yotpo surface a
// *redemption* (referred-customer) context, so we scope to referral-URL context.
const REFERRAL_URL_PARAMS = ['sref_id', 'sref'] as const
const SWELL_SETUP_EVENT = 'swell:setup'
const SOURCE = 'swell_referred_customer'
// Match useSwellSDK's SDK-load timeout so "failed to open" means the same thing across surfaces.
const SWELL_LOAD_TIMEOUT_MS = 30000

const hasReferralParam = (): boolean => {
  const params = new URLSearchParams(window.location.search)
  return REFERRAL_URL_PARAMS.some(key => params.has(key))
}

/**
 * Redemption-side popup step. Yotpo/Swell exposes no popup-open DOM event, so we use the real
 * `swell:setup` event (SDK ready) as the signal that the referred-customer redemption popup can open:
 *   - success (`referral_redemption_popup_opened`) — `swell:setup` fired (or `swellAPI` already present).
 *   - failure (`referral_redemption_popup_open_error`) — Yotpo never set up within the load timeout, so
 *     no redemption popup / coupon can happen.
 *
 * Scoped to referral-URL context (referred friend), mounted globally (`_app`), server-safe,
 * telemetry-only, emits at most once. Mirrors the SDK-loaded/load-failed pattern in `useSwellSDK`.
 */
export const useReferralRedemptionPopupTelemetry = (): void => {
  const hasFiredRef = useRef(false)

  useEffect(() => {
    if (!isBrowser() || hasFiredRef.current) return undefined
    if (!hasReferralParam()) return undefined

    const emitOpened = (): void => {
      if (hasFiredRef.current) return
      hasFiredRef.current = true
      trackReferralRedemptionPopupOpened({ source: SOURCE })
    }

    // `swell:setup` may have fired before this mounted (client-side nav / already-loaded SDK); treat an
    // available `swellAPI` as setup complete, matching useSwellSDK.
    if ((window as unknown as { swellAPI?: unknown }).swellAPI) {
      emitOpened()
      return undefined
    }

    document.addEventListener(SWELL_SETUP_EVENT, emitOpened)
    const timer = window.setTimeout(() => {
      if (hasFiredRef.current) return
      hasFiredRef.current = true
      trackReferralError(
        'redemptionPopupOpen',
        new Error(`${SWELL_SETUP_EVENT} did not fire within ${SWELL_LOAD_TIMEOUT_MS}ms`),
        { source: SOURCE },
      )
    }, SWELL_LOAD_TIMEOUT_MS)

    return () => {
      document.removeEventListener(SWELL_SETUP_EVENT, emitOpened)
      window.clearTimeout(timer)
    }
  }, [])
}

export default useReferralRedemptionPopupTelemetry
